import {IconUsers} from '@tabler/icons-react';
import React, {useRef, useState} from 'react';
import {UserStatusEnum, UserTypeEnum} from 'src/api/ProactApiClient';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {usePersonnelPermissions} from 'src/shared/auth/permissions/usePersonnelPermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {PersonnelImportModal} from '../../../../../app/personnel/PersonnelImportModal/PersonnelImportModal';
import type {VendorPersonnelTableRef} from '../../VendorView/components/VendorPersonnelTable/VendorPersonnelTable';
import {VendorPersonnelTable} from '../../VendorView/components/VendorPersonnelTable/VendorPersonnelTable';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface VendorPersonnelListProps {
  vendorId?: number;
  onRowClick?: (id: number, vendorId?: number) => void;
  disableImport?: boolean;
}

export const VendorPersonnelList: React.FC<VendorPersonnelListProps> = ({
  vendorId,
  onRowClick,
  disableImport = false,
}) => {
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const [showImportPersonnel, setShowImportPersonnel] =
    useState<boolean>(false);
  const tableRef = useRef<VendorPersonnelTableRef>(null);
  const personnelPerms = usePersonnelPermissions();

  const handleImportSuccess = () => {
    setShowImportPersonnel(false);
    setAlert(
      'Your file has been successfully uploaded. You will receive an email after it has been imported with more information.',
      'success'
    );
    tableRef.current?.refetch();
  };

  function handleAddClick(): void {
    if (vendorId) {
      navigate(PathUtility.VendorPersonnelCreate(String(vendorId)));
    } else {
      navigate(PathUtility.VendorListPersonnelCreate());
    }
  }

  return (
    <>
      <PageHeader>
        <PageHeader.Title
          icon={<IconUsers />}
          subtitle="Manage vendor personnel">
          Personnel
        </PageHeader.Title>
      </PageHeader>
      <VendorPersonnelTable
        ref={tableRef}
        identifier="vendor-personnel"
        userType={UserTypeEnum.Contractor}
        vendorId={vendorId}
        defaultColumnFilters={[
          {
            id: 'active',
            value: [UserStatusEnum.Active.toString()],
          },
        ]}
        defaultColumnVisibility={{
          jobCode: false,
          'vendor.id': false,
          createdAt: false,
          updatedAt: false,
        }}
        defaultSorting={[{id: 'lastName', desc: false}]}
        onRowClick={row => onRowClick?.(row.id, row.vendorId)}
        actionBarPreNode={
          <>
            {!disableImport && personnelPerms.import && (
              <TableToolbar.Action
                variant="import"
                onClick={() => setShowImportPersonnel(true)}
              />
            )}
            {personnelPerms.create && (
              <TableToolbar.Action variant="create" onClick={handleAddClick}>
                New Personnel
              </TableToolbar.Action>
            )}
          </>
        }
      />

      <PersonnelImportModal
        show={showImportPersonnel}
        onClose={() => setShowImportPersonnel(false)}
        onImportSuccess={handleImportSuccess}
        userType={UserTypeEnum.Contractor}
        vendorId={vendorId}
      />
    </>
  );
};
